/*    
<summary>
  This functional component "AddUser" defines the props like initial values and functions for the child form component.
  Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import scheduleTypeEnum from "../../../../constants/enums/schedule-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import {
  getAccessOrgIdFromLocalStorage,
  getTenantAccessFromLocalStorage,
} from "../../../../helpers/localstorage-helper";
import {
  IAddWeekdayFreeTextSchedule,
  IUpdateWeekdayFreeTextSchedule,
} from "../../../../models/forms/IAddUpdateWeekdayScheduler";
import { formatMessage } from "../../../../translations/format-message";
import AddUpdateFreeTextWeekdaySchedulerForm from "../../forms/add-edit-free-text-weekday-scheduler-form";
import "./add-edit-free-text-weekday-scheduler.css";

interface IProps {
  id: number;
  modalClosed: () => void;
}

const AddFreeTextWeekdayScheduler = (props: IProps) => {
  const { preferencesStore, priceSchedulerStore, mediaStore } = useStore();
  const { getTenantAccess, getFreeTextSchedulerAccess } = preferencesStore;
  const {
    AddFreeTextWeekdaySchedulerService,
    addUpdateFreeTextSchedulerState,
    priceSchedulerState,
    GetFreeTextSchedulerServiceById,
    freeTextSchedulerDetails,
    UpdateFreeTextWeekdaySchedulerService,
    resetGetFreeTextSchedulerDetail,
    resetAddUpdateFreeTextScheduler,
    GetAllFreeTextSchedulers,
    allFreeTextSchedulers,
    reset,
    allPriceScheduler,
  } = priceSchedulerStore;
  const { GetAllFolders, allFreeTextFolders, allAvailableFreeTexts } =
    mediaStore;
  const initialValues: any = freeTextSchedulerDetails;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */

  useEffect(() => {
    GetAllFolders(getTenantAccessFromLocalStorage());
    GetAllFreeTextSchedulers(getAccessOrgIdFromLocalStorage(), getFreeTextSchedulerAccess.plId); 
    if (props.id > 0) {
      GetFreeTextSchedulerServiceById(
        props.id,
        getAccessOrgIdFromLocalStorage(),
      );
    }
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateUserSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateFreeTextSchedulerState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateFreeTextSchedulerState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "userDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (priceSchedulerState.error) {
      toast.error(formatMessage(priceSchedulerState.error));
    }
  }, [priceSchedulerState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateUserError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateFreeTextSchedulerState.error) {
      toast.error(formatMessage(addUpdateFreeTextSchedulerState.error));
      reset();
    }
  }, [addUpdateFreeTextSchedulerState.error]);
  /**
   * This function adds the branch when "Id < 0" and updates the Existing branch when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    var addObj: IAddWeekdayFreeTextSchedule = {
      PlId: getFreeTextSchedulerAccess.plId,
      ScheduleType: scheduleTypeEnum.Weekday,
      Weekday: values.Weekday,
      StartDate: null,
      EndDate: null,
      StartTime: values.time,
      EndTime: null,
      FolderId: JSON.parse(values.FolderId),
      FreeTextId1: JSON.parse(values.Data1) == -1 ? null : JSON.parse(values.Data1),
      FreeTextId2: JSON.parse(values.Data2) == -1 ? null : JSON.parse(values.Data2),
      FreeTextId3: JSON.parse(values.Data3) == -1 ? null : JSON.parse(values.Data3),
      FreeTextId4: JSON.parse(values.Data4) == -1 ? null : JSON.parse(values.Data4),
      FreeTextId5: JSON.parse(values.Data5) == -1 ? null : JSON.parse(values.Data5),
      FreeTextId6: JSON.parse(values.Data6) == -1 ? null : JSON.parse(values.Data6),
      OrganizationId: getAccessOrgIdFromLocalStorage()?.toLowerCase(),
    };
    var updateObj: IUpdateWeekdayFreeTextSchedule = {
      FtsId: props.id,
      PlId: getFreeTextSchedulerAccess.plId,
      ScheduleType: scheduleTypeEnum.Weekday,
      Weekday: values.Weekday,
      StartDate: null,
      EndDate: null,
      StartTime: values.time,
      EndTime: null,
      FolderId: JSON.parse(values.FolderId),
      FreeTextId1: JSON.parse(values.Data1) == -1 ? null : JSON.parse(values.Data1),
      FreeTextId2: JSON.parse(values.Data2) == -1 ? null : JSON.parse(values.Data2),
      FreeTextId3: JSON.parse(values.Data3) == -1 ? null : JSON.parse(values.Data3),
      FreeTextId4: JSON.parse(values.Data4) == -1 ? null : JSON.parse(values.Data4),
      FreeTextId5: JSON.parse(values.Data5) == -1 ? null : JSON.parse(values.Data5),
      FreeTextId6: JSON.parse(values.Data6) == -1 ? null : JSON.parse(values.Data6),
      OrganizationId: getAccessOrgIdFromLocalStorage()?.toLowerCase(),
    };
    if (props.id < 0) AddFreeTextWeekdaySchedulerService(addObj);
    else UpdateFreeTextWeekdaySchedulerService(updateObj);
  };

  /**
   * This function closes the pop up modal and reset getbrachDetails, addUpdate branch observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetGetFreeTextSchedulerDetail();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddUpdateFreeTextWeekdaySchedulerForm
        initialValues={initialValues}
        id={props.id}
        allPriceScheduler={allFreeTextSchedulers}
        allAvailableImages={allAvailableFreeTexts}
        allFreeTextFolders={allFreeTextFolders}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        isLoading={
          addUpdateFreeTextSchedulerState.inProgress ||
          priceSchedulerState.inProgress
        }
      />
    </React.Fragment>
  );
};

export default observer(AddFreeTextWeekdayScheduler);
